import { Component, inject, Inject, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet';
import { environment } from '@fantaz/shared/app-config';
import { AuthFacade } from '@fantaz/shared/auth';
import { VIDEOS_URLs } from '@fantaz/shared/config';
import { FzGoogleTagManagerService } from '@fantaz/shared/data-access/api';
import {
  CurrentUser,
  GAEventKeys,
  ShareOptions,
} from '@fantaz/shared/data-access/models';
import { ToastService } from '@fantaz/shared/utils/toast';
import { take } from 'rxjs';
import { ShareOption } from './share.model';
import { ShareAction, ShareService } from './share.service';

@Component({
  selector: 'fz-share-dialog',
  templateUrl: './share-dialog.component.html',
  styleUrls: ['./share-dialog.component.scss'],
})
export class ShareDialogComponent implements OnInit {
  readonly #authFacade = inject(AuthFacade);
  currentUser$ = this.#authFacade.currentUser$;
  inviteForm: UntypedFormGroup;
  inviteResponse: string;
  invitationError: boolean;
  inviteEmail: string;
  female: boolean;
  showVideos = true;
  visibleEmail = false;
  inviting = false;
  profileLink = '';
  iframeWith = 255;
  iframeHeight = 133;
  genderSelection: string;
  videoSelected = VIDEOS_URLs['female_intro'].id;
  femaleVideoId = VIDEOS_URLs['female_intro'].id;
  maleVideoId = VIDEOS_URLs['male_intro'].id;
  introFemaleVideoUrl: string;
  introMaleVideoUrl: string;
  currentUser: CurrentUser | null | undefined;
  shareParams: ShareOptions;
  canShareWithFantazEmail: boolean;
  canWebShare: boolean;
  shareBy: any;
  shareOptions: ShareOption[];
  isPromote: boolean;
  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA)
    public data: {
      shareOptions: ShareOption[];
      options: ShareOptions;
      params?: { callback?: (params?: any) => void };
    },
    private bottomSheetRef: MatBottomSheetRef<ShareDialogComponent>,
    public shareService: ShareService,
    private toastService: ToastService,
    private gtmService: FzGoogleTagManagerService,
  ) {
    this.introFemaleVideoUrl = VIDEOS_URLs['female_intro'].path;
    this.introMaleVideoUrl = VIDEOS_URLs['male_intro'].path;
    this.isPromote = this.data.options.type === 'promote';
  }

  ngOnInit(): void {
    if (this.data.options.currentUser) {
      this.currentUser = this.data.options.currentUser;
      this.init();
    } else {
      this.getUser();
    }
  }

  getUser(): void {
    this.currentUser$.pipe(take(2)).subscribe((user) => {
      this.currentUser = user as CurrentUser;
      this.init();
    });
  }

  init(): void {
    this.getUserType();
    this.inviteForm = new UntypedFormGroup({
      email: new UntypedFormControl('', [
        Validators.required,
        Validators.email,
      ]),
    });
    this.updateInvitationLink();
  }

  getUserType() {
    if (this.currentUser) {
      this.canShareWithFantazEmail =
        this.currentUser?.user?.verificationStatus?.type === 'APPROVED';
      if (!this.canShareWithFantazEmail) {
        this.shareOptions = this.data.shareOptions.filter(
          (o) => o.function !== 'onFantaZEmailShare',
        );
      } else {
        this.shareOptions = this.data.shareOptions;
      }
    } else {
      this.shareOptions = this.data.shareOptions.filter(
        (o) => o.function !== 'onFantaZEmailShare',
      );
    }
  }

  inviteMember() {
    const { email } = this.inviteForm.value;
    if (this.currentUser && this.inviteForm.valid) {
      this.inviting = true;
      this.shareService
        .inviteMember(email, this.profileLink, this.currentUser)
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.inviting = false;
            this.inviteForm.reset();
            this.inviteResponse = 'Invite successfully sent!';

            if (this.data && this.data.params && this.data.params.callback) {
              this.data.params.callback();
            }
            this.toastService.openSnackBar('The invitation successfully sent!');
          },
          error: (error: { status: number }) => {
            this.inviting = false;
            this.invitationError = true;
            if (error.status == 429) {
              this.inviteResponse =
                'Unable to send message, you have exceeded daily invitation limit, please try again tomorrow.';
            }
          },
        });
    }
  }

  onFantaZShare() {
    this.visibleEmail = !this.visibleEmail;
  }

  genderVideoChange(event: any) {
    this.videoSelected = event.value;
    this.updateInvitationLink();
  }

  onShowVideosChange() {
    this.updateInvitationLink();
  }

  copyLink() {
    this.shareService.onCopyLinkShare(this.shareParams);
  }

  updateInvitationLink() {
    let joinLinkBase = environment.baseURL + '/join';
    if (this.isPromote) {
      const url = this.shareService.getInvitationLink({
        isPromote: this.isPromote,
        username: this.currentUser?.user?.username,
        baseUrl: joinLinkBase,
      });
      const params = url.searchParams;

      if (this.showVideos) {
        if (this.videoSelected == VIDEOS_URLs['male_intro'].id) {
          params.append('mg', VIDEOS_URLs['male_intro'].id);
        }

        if (this.videoSelected == VIDEOS_URLs['female_intro'].id) {
          params.append('mg', VIDEOS_URLs['female_intro'].id);
        }
      }

      this.profileLink = url.toString();
      this.shareParams = {
        title: 'FantaZ',
        text: 'Promote Link',
        url: this.profileLink,
        currentUser: this.currentUser,
      };
    } else {
      const url = new URL(this.data.options.url);
      this.setInviteBy(url);
      this.profileLink = url.toString();
    }
  }
  private setInviteBy(url: URL) {
    const params = url.searchParams;
    if (
      this.currentUser &&
      this.currentUser.user &&
      this.currentUser.user.username
    ) {
      if (!url.searchParams.has('inviteBy')) {
        params.append('inviteBy', this.currentUser?.user.username);
      }
    }
  }

  close() {
    this.bottomSheetRef.dismiss('closed');
  }

  onShare(item: string) {
    if (item === 'onFantaZEmailShare') {
      this.onFantaZShare();
    } else {
      const action = item as ShareAction;
      this.shareService.shareResult({ action, url: this.profileLink });
    }
  }

  onVideoEnded() {
    this.sendGAEvent(GAEventKeys.ENDED_PROMOTE_VIDEO);
  }

  onVideoPlayed() {
    this.sendGAEvent(GAEventKeys.PLAY_PROMOTE_VIDEO);
  }

  onVideoPaused() {
    this.sendGAEvent(GAEventKeys.PAUSED_PROMOTE_VIDEO);
  }

  sendGAEvent(eventName: GAEventKeys) {
    const event = {
      event: eventName,
      video_id: this.videoSelected,
      timestamp: Date.now(),
      page: 'Promote',
      user_id: this.currentUser?.user.uuid,
      user_name: this.currentUser?.user.username,
    };
    this.gtmService.pushTag(event);
  }
}
